import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { injectable } from 'inversify';
import fileDownload from 'js-file-download';
import { remove } from 'lodash';
import { merge } from 'lodash/fp';
import { IConfiguration } from '../../configuration/config.types';
import { ICurrentWorkspaceStore } from '../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { delay } from '../helpers/function.helpers';
import { decodeImage } from '../helpers/imageDecoder.helpers';
import { ACDLError, CancelledError } from '../models/error.model';
import { IAuthStore } from '../modules/auth/auth.store';
import { toMBytes } from '../modules/datasets/helpers/image.helpers';
import { IApiPolicyProvider, IPolicy } from './api.policy';
import { CacheManager, ICacheManager } from './cacheManager';
import { ICryptoService } from './crypto.service';

interface IImageFile {
  blob: Blob;
  attributes: IImageAttributes;
}

interface IImageAttributes {
  channels: number;
  isCached: boolean;
  downloadSpeed?: number;
}

export interface IApiService {
  getUrl(suffix: string): string;
  getAsync<TResult>(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError>;
  deleteAsync<TResult>(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError>;
  getFileAsync(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy, overrideMime?: string): Promise<void | ACDLError>;
  getImageAsync(fullUrl: string, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<IImageFile | ACDLError>;
  postAsync<TResult = void>(urlSuffix: string, body?: unknown, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError>;
  config: IConfiguration;
}

@injectable()
export abstract class ApiServiceBase implements IApiService {
  constructor(
    public config: IConfiguration,
    protected authStore: IAuthStore,
    protected currentWorkspaceStore: ICurrentWorkspaceStore,
    protected apiPolicy: IApiPolicyProvider,
    protected cacheManager: ICacheManager,
    protected cryptoService: ICryptoService
  ) {}

  private static currentImageRequests: string[] = [];
  private static currentImageRequestsDurations: { url: string; duration: number }[] = [];

  public abstract getUrl(urlSuffix: string): string;

  public async getAsync<TResult>(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError> {
    try {
      const result: AxiosResponse<TResult> = await axios(this.getUrl(urlSuffix), this.addCommonHeaderToConfig(config));
      this.updateAuthToken(result);
      return result.data;
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  public async deleteAsync<TResult>(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError> {
    try {
      const result: AxiosResponse<TResult> = await axios.delete(this.getUrl(urlSuffix), this.addCommonHeaderToConfig(config));
      this.updateAuthToken(result);
      return result.data;
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  public async getFileAsync(urlSuffix: string, config?: AxiosRequestConfig, customPolicy?: IPolicy, overrideMime?: string): Promise<void | ACDLError> {
    try {
      const requestConfig = config || {};
      requestConfig.responseType = 'arraybuffer';

      const result: AxiosResponse = await axios(this.getUrl(urlSuffix), this.addCommonHeaderToConfig(requestConfig));
      this.updateAuthToken(result);
      fileDownload(result.data, this.getFileNameFromResponse(result), overrideMime);
      await Promise.resolve();
      return undefined;
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  public async getImageAsync(fullUrl: string, customPolicy?: IPolicy): Promise<IImageFile | ACDLError> {
    const cacheAvailable = await CacheManager.checkCacheAvailabilityAsync();

    while (ApiServiceBase.currentImageRequests.includes(fullUrl)) {
      // eslint-disable-next-line no-await-in-loop -- image already in the download queue
      await delay(10);
    }

    if (this.currentWorkspaceStore.currentWorkspace.encryption.encrypted) {
      // eslint-disable-next-line no-await-in-loop -- wait for image to be decrypted
      while (!(await this.cryptoService.checkKey(this.currentWorkspaceStore.currentWorkspace.id, this.currentWorkspaceStore.currentWorkspace.id))) {
        // eslint-disable-next-line no-await-in-loop -- wait for image to be decrypted
        await delay(1000);
      }
    }

    ApiServiceBase.currentImageRequests.push(fullUrl);

    if (cacheAvailable) {
      const cachedArrayBuffer = await this.cacheManager.tryGetFromCacheAsync(fullUrl);
      if (cachedArrayBuffer) {
        remove(ApiServiceBase.currentImageRequests, (x) => x === fullUrl);
        const duration = this.getCachedDuration(fullUrl);
        return {
          blob: new Blob([cachedArrayBuffer]),
          attributes: this.getAttributes(cachedArrayBuffer, true, duration),
        };
      }
    }

    const response = await this.fetchImageAsync(fullUrl, customPolicy);

    if (response instanceof ACDLError || response.response.status !== 200) {
      remove(ApiServiceBase.currentImageRequests, (x) => x === fullUrl);
      if (response instanceof ACDLError) {
        return response;
      }
      return new ACDLError('Unknown error');
    }

    let arrayBufferResponse = await response.response.arrayBuffer();

    arrayBufferResponse = await this.cryptoService.decrypt(this.currentWorkspaceStore.currentWorkspace.id, arrayBufferResponse);

    arrayBufferResponse = await this.removeExifTags(arrayBufferResponse);

    let savedInCache = false;

    if (cacheAvailable) {
      try {
        await this.cacheManager.makeRoomInCacheAsync(arrayBufferResponse.byteLength);
        await this.cacheManager.putInCacheAsync(fullUrl, arrayBufferResponse);
        savedInCache = true;
      } catch {
        // igonre
      }
    }

    this.setCachedDuration(fullUrl, response.duration);
    remove(ApiServiceBase.currentImageRequests, (x) => x === fullUrl);
    return {
      blob: new Blob([arrayBufferResponse]),
      attributes: this.getAttributes(arrayBufferResponse, savedInCache, response.duration),
    };
  }

  /*
  Ten kod powstał po to, aby wyświetlać czas pobierania zdjęć w przypadku, kiedy użytkownik otwierał zdjęcie, które już było w trakcie pobierania przez eager loading.
  Zasada działania: po pobraniu zdjęcia przez eager loading do tablicy wrzucana jest jego prędkość pobierania.
  Kolejne żądanie pobrania zdjęcia sprawdza czy w tablicy jest czas pobierania zdjęcia, jeśli jest to go wyświetla i usuwa z tablicy.
  */
  private getCachedDuration(url: string): number | undefined {
    const index = ApiServiceBase.currentImageRequestsDurations.findIndex((x) => x.url === url);
    let duration: number | undefined = undefined;
    if (index > -1) {
      duration = ApiServiceBase.currentImageRequestsDurations[index].duration;
      ApiServiceBase.currentImageRequestsDurations.splice(index, 1);
    }
    return duration;
  }

  private setCachedDuration(url: string, duration: number): void {
    ApiServiceBase.currentImageRequestsDurations.push({ url, duration });
  }

  private getAttributes(arrayBuffer: ArrayBuffer, isCached: boolean, downloadDuration?: number): IImageAttributes {
    const metadata = decodeImage(arrayBuffer);

    const speed = downloadDuration ? (toMBytes(arrayBuffer.byteLength) * 8) / (downloadDuration / 1000) : downloadDuration;

    let channels = metadata.channels;

    if (!channels) {
      const colorType = metadata.colorType;
      switch (colorType) {
        case 0:
        case 3:
          channels = 1;
          break;
        case 2:
          channels = 3;
          break;
        case 4:
          channels = 2;
          break;
        case 6:
          channels = 4;
          break;
        case undefined:
          break;
      }
    }

    return {
      isCached,
      channels: channels || 0,
      downloadSpeed: speed,
    } as IImageAttributes;
  }

  public async postAsync<TResult = object>(
    urlSuffix: string,
    body?: unknown,
    config?: AxiosRequestConfig,
    customPolicy?: IPolicy
  ): Promise<TResult | ACDLError> {
    try {
      const result: AxiosResponse<TResult> = await axios.post(this.getUrl(urlSuffix), body, this.addCommonHeaderToConfig(config));
      this.updateAuthToken(result);
      return result.data;
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  public async upload<TResult = object>(urlSuffix: string, body?: unknown, config?: AxiosRequestConfig, customPolicy?: IPolicy): Promise<TResult | ACDLError> {
    try {
      const result: AxiosResponse<TResult> = await axios.post(this.getUrl(urlSuffix), body, this.addCommonHeaderToConfig(config));
      this.updateAuthToken(result);
      return result.data;
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  private async fetchImageAsync(fullUrl: string, customPolicy?: IPolicy): Promise<{ response: Response; duration: number } | ACDLError> {
    try {
      const downloadStartTimestamp = performance.now();
      const response = await fetch(fullUrl, { headers: new Headers({ Authorization: `Bearer ${this.authStore.token}` }) });
      const downloadEndTimestamp = performance.now();
      return { response, duration: downloadEndTimestamp - downloadStartTimestamp };
    } catch (error) {
      return await this.handleError(error, customPolicy);
    }
  }

  private getFileNameFromResponse(response: AxiosResponse): string {
    const disposition = response.request.getResponseHeader('Content-Disposition');
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename\*=UTF-8''([^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches !== null && matches !== undefined && matches[1]) {
        return decodeURI(matches[1]);
      }
    }
    return '';
  }

  private addCommonHeaderToConfig(config: AxiosRequestConfig = {}): AxiosRequestConfig<unknown> & { headers: { Server: string } } {
    const newConfig = this.authStore.token ? merge(config, { headers: { Authorization: `Bearer ${this.authStore.token}` } }) : config;

    return merge(newConfig, { headers: { Server: this.config.version } });
  }

  private updateAuthToken(response: AxiosResponse): void {
    if (response.headers.authorization) {
      this.authStore.updateToken(response.headers.authorization);
    }
  }

  private async handleError(error: unknown, customPolicy?: IPolicy): Promise<ACDLError> {
    if (error instanceof AxiosError) {
      if (error.code === 'ERR_CANCELED') {
        return new CancelledError();
      }
      if (error.response) {
        const effectivePolicy: IPolicy = Object.assign(this.apiPolicy.getGlobal(), customPolicy || {});

        if (error.response.status in effectivePolicy) {
          return await effectivePolicy[error.response.status](error);
        }

        return new ACDLError(error.response.statusText, error.response.data);
      }
    }
    return new ACDLError('Fatal error', undefined);
  }

  private async removeExifTags(buffer: ArrayBuffer): Promise<ArrayBuffer> {
    const dv = new DataView(buffer);
    let offset = 0;
    let recess = 0;
    const pieces: { recess: number; offset: number }[] = [];
    let i = 0;
    if (dv.getUint16(offset) === 0xffd8) {
      offset += 2;
      let app1 = dv.getUint16(offset);
      offset += 2;
      while (offset < dv.byteLength) {
        if (app1 === 0xffe1) {
          pieces[i] = { recess, offset: offset - 2 };
          recess = offset + dv.getUint16(offset);
          i += 1;
        } else if (app1 === 0xffda) {
          break;
        }
        offset += dv.getUint16(offset);
        app1 = dv.getUint16(offset);
        offset += 2;
      }
      if (pieces.length > 0) {
        const newPieces: ArrayBuffer[] = [];
        pieces.forEach((v) => {
          newPieces.push(buffer.slice(v.recess, v.offset));
        });
        newPieces.push(buffer.slice(recess));

        return await new Blob(newPieces).arrayBuffer();
      }
    }
    return buffer;
  }
}
